import { FC, MouseEvent } from 'react';
import classNames from 'classnames';

import { useEcomStoreSelector, useUiStoreSelector } from '@/data';
import { useSelectedStore, useShoppingMode } from '@/hooks';

import { IconSvg } from '../IconSvg';
import { Button } from '../Button';
import { addressAsString, ILocalAddress } from '../molecules/address';

import styles from './CartShoppingInfo.module.scss';

const CartShoppingInfo: FC = () => {
  const { onlyDelivery, onlyPickup, deliveryAndPickup } = useShoppingMode();
  const { store } = useSelectedStore();
  const {
    delivery_details,
    delivery_address,
    setState: setEcomState,
  } = useEcomStoreSelector(['delivery_details', 'delivery_address']);
  const { setState: setUIState } = useUiStoreSelector([]);
  const shoppingMethod = delivery_details.pickup ? 'Pickup' : 'Delivery';
  const address = delivery_details.pickup
    ? store?.full_address
    : addressAsString(delivery_address as ILocalAddress);

  const goToStoreSelection = (event: MouseEvent) => {
    event.stopPropagation();

    const shouldUsePickup =
      onlyPickup || (deliveryAndPickup && delivery_details.delivery);
    const temporaryFulfillmentMethod = shouldUsePickup ? 'PICKUP' : 'DELIVERY';

    setEcomState({
      temporary_fulfillment_method: temporaryFulfillmentMethod,
    });
    setUIState({ is_cart_open: false, is_store_location_open: true });
  };

  const buttonAriaLabel = store
    ? `Change ${shoppingMethod.toLowerCase()} location: ${address}`
    : 'Select your store';

  return store ? (
    <Button
      ariaLabel={buttonAriaLabel}
      className={styles.store_locator__button}
      onClick={goToStoreSelection}
      size="md"
      type="button"
    >
      <div className={styles.store_locator__button_content}>
        {!onlyDelivery && (
          <div
            aria-hidden="true"
            className={classNames(styles.shopping_mode_indicator, {
              [styles.selected]: delivery_details.pickup,
            })}
          >
            <IconSvg aria-hidden="true" name="store" />
          </div>
        )}
        {!onlyPickup && (
          <div
            aria-hidden="true"
            className={classNames(styles.shopping_mode_indicator, {
              [styles.selected]: delivery_details.delivery,
            })}
          >
            <IconSvg aria-hidden="true" name="car" />
          </div>
        )}
        <div className={styles.shopping_info}>
          <div>
            <span className={styles.shopping_info__title}>
              {`${shoppingMethod} at`}
            </span>{' '}
            &nbsp;
            {delivery_details.pickup && (
              <span className={styles.shopping_info__address}>
                {store.name}
              </span>
            )}
          </div>
          <div className={styles.shopping_info__address} title={address}>
            {address}
          </div>
        </div>
      </div>
    </Button>
  ) : (
    <Button
      ariaLabel={buttonAriaLabel}
      className={styles.select_store_button}
      color="link"
      onClick={goToStoreSelection}
      size="sm"
    >
      Select your store
    </Button>
  );
};

export { CartShoppingInfo };
